import { useMemo } from 'react'
import { useGetPlanSettings } from './requests'

/**
 * Conversion factors from the PDF plan's paper space into world space, derived
 * from the project's plan settings (font size in points, plan scale).
 */
export interface PlanLabelPdfMetrics {
  /** em size of the plan label font in world units */
  emWorld: number
  /** world meters per PDF point at the plan scale */
  ptToWorld: number
}

/** 1pt = 1/72 inch on paper, in meters */
const POINT_TO_PAPER_METERS = 0.0254 / 72

/**
 * World-space equivalents of the exported plan's font metrics, so the
 * floorplan labels can render at exactly the size of the PDF plan labels.
 *
 * Same fallbacks as `useLabelScaleFromPlanSettings` (which returns just
 * `emWorld` and stays for the non-label plan overlays).
 */
const usePlanLabelPdfMetrics = (): PlanLabelPdfMetrics & {
  /** plan label font size in points */
  fontSize: number
} => {
  const { data: planSettings } = useGetPlanSettings()

  return useMemo(() => {
    const fontSize = planSettings?.fontSize || 8
    const scale = planSettings?.scale || 1
    const ptToWorld = POINT_TO_PAPER_METERS * scale
    return { emWorld: fontSize * ptToWorld, ptToWorld, fontSize }
  }, [planSettings])
}

export default usePlanLabelPdfMetrics
